import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, PaperPlaneRight, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AICopilot = ({ onClose }) => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    startInterview();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startInterview = async () => {
    try {
      const response = await axios.post(`${API}/candidate/interview/start`);
      setSessionId(response.data.session_id);
      setMessages([{ role: 'ai', content: response.data.message }]);
    } catch (error) {
      toast.error('Failed to start interview session');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/candidate/interview/message?session_id=${sessionId}`,
        { message: userMessage }
      );
      setMessages(prev => [...prev, { role: 'ai', content: response.data.response }]);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="ai-copilot-modal">
      <div className="bg-white w-full max-w-3xl h-[80vh] flex flex-col rounded-none border-2 border-[#0033FF]">
        {/* Header */}
        <div className="p-6 border-b border-black/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0033FF] flex items-center justify-center">
              <Sparkle size={20} weight="duotone" className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold" data-testid="copilot-title">AI Career Copilot</h3>
              <p className="text-xs text-[#A1A1AA]">Practice interviews and get feedback</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 transition-colors rounded-none"
            data-testid="close-copilot-button"
          >
            <X size={24} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${idx}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-[#0033FF] text-white'
                    : 'bg-[#F9F9FB] text-[#0A0A0A] border border-black/5'
                }`}
              >
                <p className="font-light whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#F9F9FB] border border-black/5 px-4 py-3">
                <p className="font-light text-[#A1A1AA]">AI is typing...</p>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-6 border-t border-black/10">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your response..."
              className="flex-1 px-4 py-3 bg-[#F9F9FB] border border-black/10 rounded-none focus:ring-2 focus:ring-[#0033FF] focus:border-transparent outline-none font-light"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-[#0033FF] text-white px-6 py-3 hover:bg-[#002AE6] transition-colors disabled:opacity-50 rounded-none"
              data-testid="send-message-button"
            >
              <PaperPlaneRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICopilot;
